import { InAppBrowser, InAppBrowserObject } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { getLogger } from "@shared/logging";
import { environment } from "src/environments/environment";

const log = getLogger("ProxyReceive")

export class ProxyReceiveError extends Error {
  constructor(message: string, public UserFriendlyMessage?: string, public referenceCode?: string) {
    super(message);
    this.name = "ProxyReceiveError";
  }
}

type ProxyReceiveOptions = {
  /**
   * The name of the function to be rendered in the body
   */
  functionName?: string
  /**
   * Whether or not the script should close the window when the function is called
   */
  debugMode?: boolean,
  /**
   * The domain from which the `postMessage` call must have been sent.
   * Generally should be `environment.apiBase`
   */
  eventSenderSourceDomain?: string
  targetURL: string
}

type ProxyDTOMetadata = {
  version: number
  functionName: string
  debugMode: boolean
}

export type ProxyDTOSerialization = {
  metadata: ProxyDTOMetadata, 
  data: any
}

/**
 * This MUST match with the version in the backend SSO response handler
 * See backend @ src/classes/PopupCrossOriginDTOProxy.ts
 */
const DTO_PROXY_VERSION = 1;

/**
 * Checks for the presence of the Cordova InAppBrowser plugin.
 * 
 * Note: The Capacitor Browser plugin lacks the required interop functionality (e.g., postMessage support),
 * so we rely on the Cordova InAppBrowser plugin for this feature.
 *
 * Capacitor Browser plugin: https://capacitorjs.com/docs/apis/browser
 * Awesome Cordova InAppBrowser plugin: https://danielsogl.gitbook.io/awesome-cordova-plugins/in-app-browser
 * Apache Cordova InAppBrowser plugin: https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-inappbrowser/
 */
export function CheckForInAppBrowser(): boolean {
  const w = window as any;
  const hasiab = "cordova" in w && "InAppBrowser" in w.cordova;
  log.debug(`InAppBrowser plugin installed: ${hasiab}`, w?.cordova?.InAppBrowser);
  return hasiab
}

/**
 * Registers the event handler on the supplied `Window` object, and 
 * resolves when the data object has been received.
 * 
 * 
 * @param options 
 * @param window 
 * @returns 
 */
export function ProxyReceiveObject(options: ProxyReceiveOptions, window: Window, iab?: InAppBrowser ): Promise<ProxyDTOSerialization> {
  if ( !("functionName" in options) ) {
    options.functionName = "ProxySendObject"
  }
  if (! ("eventSenderSourceDomain" in options)) {
    options.eventSenderSourceDomain = environment.apiBase
  }
  if (iab && CheckForInAppBrowser()) {
    return ProxyReceiveObjectMobile(options,iab);
  }
  else {
    return ProxyReceiveObjectNative(options,window);
  } 
}


function ProcessResponse(event: any, options: ProxyReceiveOptions): ProxyDTOSerialization{
  if (!event) {
    throw new ProxyReceiveError("No event received")
  }
  if (!("data" in event && "metadata" in event)) {
    throw new ProxyReceiveError("Invalid event received")
  }

  const deserialized = event as ProxyDTOSerialization;
  if (deserialized.data.error) {
    throw new ProxyReceiveError("Failed to receive data", deserialized.data.UserFriendlyMessage, deserialized.data.referenceCode);
  }
  if (deserialized.metadata.version != DTO_PROXY_VERSION) {
    throw new ProxyReceiveError("Mismatch between DTOProxy versions")
  }
  if (deserialized.metadata.functionName != options.functionName) {
    throw new ProxyReceiveError("Mismatch between DTOProxy configuration")
  }
  if (typeof deserialized.metadata.debugMode != "boolean") {
    if (typeof options.debugMode == "boolean") {
      deserialized.metadata.debugMode = options.debugMode;
    }
    else {
      deserialized.metadata.debugMode = false;
    }
  }
  log.debug(`Received Proxy DTO: ${JSON.stringify(deserialized.metadata)}`)
  return deserialized;  
}


async function ProxyReceiveObjectNative(options: ProxyReceiveOptions, window: Window): Promise<ProxyDTOSerialization> {
  log.debug(`Setting up default browser to receive data from ${options.targetURL}`)
  let resolve: ()=>void;
  let reject:(reason?: any) => void;

  const p = new Promise<void>((res,rej) => {
    resolve = res;
    reject = rej;
  });
  let deserialized: ProxyDTOSerialization = null; 
  const handleMessage = (event: MessageEvent) => {
    if (event.origin+"/" != options.eventSenderSourceDomain) {
      log.debug(`Ignoring event from unexpected origin: ${event.origin}`)
      return;
    }
    if (!event.data) {
      log.debug("No data in event")
      return;
    }
    try {
      deserialized = ProcessResponse(event.data, options)
      resolve();
    }
    catch (e) {
      reject(e);
    }
    finally {
      popup.close();
    }
  };
  window.addEventListener("message", handleMessage);
  let popup = window.open(options.targetURL,"Self","width=600,height=500");
  if (!popup || popup.closed || typeof popup == 'undefined') {
    throw new ProxyReceiveError("Popup window was blocked")
  }
  await p;
  return deserialized;
}

/**
 * Registers the event handler on the supplied `Window` object, and 
 * resolves when the data object has been received.
 * 
 * 
 * @param options 
 * @param window 
 * @returns 
 */
async function ProxyReceiveObjectMobile(options: ProxyReceiveOptions, iab: InAppBrowser): Promise<ProxyDTOSerialization> {
  log.debug(`Setting up InAppBrowser to receive data from ${options.targetURL}`)
  let resolve: ()=>void;
  let reject:(reason?: any) => void;
  const p = new Promise<void>((res,rej) => {
    resolve = res;
    reject = rej;
  });
  let deserialized: ProxyDTOSerialization = null; 
  const browser = iab.create(
    options.targetURL,
    '_blank', {
      location: 'no',
      cleardata: 'yes',
      zoom: 'no',
      useWideViewPort: 'no'
    }
  );
  if (!(browser instanceof InAppBrowserObject)) {
    throw new ProxyReceiveError("Unable to create InAppBrowser instance")
  }
  
  const pollPage = async () => {
    /**
     * Check if options.functionName is defined in the page
     * If it is, we can assume that the page has loaded and we can call the function to get the data
     */
    const event = await browser.executeScript({
        code: `(() => {
        if (typeof ${options.functionName} === "function") { 
          return ${options.functionName}();
        }
        return "ProxyFunctionNotFound";
      })()`,
    });
    if (event == "ProxyFunctionNotFound") {
      /**
       * We received the loadstop event, but the function was not found; 
       * so the user was probably directed to a login page (which finished loading).
       * 
       * We expect the user to eventually end up at the page generated by backend
       * with the function name defined in options.functionName.
       * 
       */
      log.debug("Proxy function not found.")
      return
    }
    if (!Array.isArray(event) && event.length == 0) {
      throw new ProxyReceiveError("Invalid event received")
    }
    try {
      deserialized = ProcessResponse(event[0], options)
      resolve()
    }
    catch (err) {
      reject(err);
    }
    finally {
      browser.close();
    }
  }
  browser.on('loadstop').subscribe(pollPage);
  await p;
  return deserialized; 
}

export function ExtractErrorFromHTMLPayload(payload: string): ProxyReceiveError {
  try {
    // Extract the script content from the HTML payload
    const scriptMatch = payload.match(/<script>([\s\S]*?)<\/script>/);
    if (!scriptMatch || scriptMatch.length < 2) {
      throw new Error("No script tag found in the payload");
    }

    const scriptContent = scriptMatch[1];

    // Extract the proxyData object from the script content
    const proxyDataMatch = scriptContent.match(/const proxyData\s*=\s*(\{[\s\S]*?\});/);
    if (!proxyDataMatch || proxyDataMatch.length < 2) {
      throw new Error("No proxyData object found in the script content");
    }

    const proxyDataString = proxyDataMatch[1];
    const proxyData = JSON.parse(proxyDataString);

    // Validate the extracted proxyData
    if (!proxyData.data || !proxyData.data.error) {
      throw new Error("Invalid proxyData format or no error present");
    }

    // Create and return a ProxyReceiveError with the extracted details
    return new ProxyReceiveError(
      "Failed to receive data",
      proxyData.data.UserFriendlyMessage,
      proxyData.data.referenceCode
    );
  } catch (err) {
    log.error("Error extracting error from HTML payload:", err);
    return new ProxyReceiveError("Failed to parse error from HTML payload");
  }
}